import { dynamicPackageAttractionLineBillableToCustomer } from "../packageAttractionRulesDisplay";
import { mergeAttractionPricingLayers } from "./mergeAttractionPricingLayers.js";
import { resolveChildUnitFromSources } from "./attractionPricingContract.js";
import { resolveOptionalUnitPriceIls } from "./resolveOptionalUnitPriceIls";

/**
 * @param {unknown} v
 * @returns {number|null}
 */
function pickPositive(v) {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** @param {unknown} v */
function pickNonNegativeFinite(v) {
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/**
 * @param {Record<string, unknown>|null|undefined} obj
 * @param {string[]} keys
 */
function firstPositiveFromKeys(obj, keys) {
  if (!obj || typeof obj !== "object") return null;
  for (const k of keys) {
    const p = pickPositive(obj[k]);
    if (p != null) return p;
  }
  return null;
}

/**
 * sell_value / price_value כשאין price_adult — rule, catalog, merged.
 * @param {Record<string, unknown>|null|undefined} rule
 * @param {Record<string, unknown>} merged
 */
function pickScalarUnitPriceFromApi(rule, merged) {
  const cat =
    rule && typeof rule === "object" && rule.catalog && typeof rule.catalog === "object"
      ? rule.catalog
      : null;
  return (
    pickPositive(rule?.sell_value) ??
    pickPositive(rule?.price_value) ??
    pickPositive(cat?.sell_value) ??
    pickPositive(cat?.price_value) ??
    pickPositive(merged.sell_value) ??
    pickPositive(merged.price_value)
  );
}

/**
 * מחירי יחידה למבוגר/ילד (ILS) + האם ילד זכאי (לפי קיום price_child במקור).
 * אין fallback ממבוגר לילד; אין העתקת base_price לילד אוטומטית.
 *
 * @param {Record<string, unknown>|null|undefined} rule
 * @param {Record<string, unknown>|null|undefined} line
 * @returns {{
 *   adultPrice: number|null,
 *   childPrice: number|null,
 *   childEligible: boolean,
 *   currency: string,
 * }}
 */
export function resolveAdultChildUnitPricesIls(rule, line) {
  const currency = "ILS";

  const catalog =
    rule && typeof rule === "object" && rule.catalog && typeof rule.catalog === "object"
      ? rule.catalog
      : null;
  const snap =
    line && line.rule_snapshot && typeof line.rule_snapshot === "object"
      ? line.rule_snapshot
      : null;

  if (line && typeof line === "object" && dynamicPackageAttractionLineBillableToCustomer(line)) {
    const tg = pickPositive(line.total_gross);
    return {
      adultPrice: tg,
      childPrice: null,
      childEligible: false,
      currency,
    };
  }

  const merged = mergeAttractionPricingLayers(rule, catalog, snap);

  const adultKeysNoScalar = [
    "price_adult",
    "priceAdult",
    "adult_price_ils",
    "unit_price_adult_ils",
    "dynamic_package_addon_price_adult_ils",
    "retail_price_ils",
    "customer_price_ils",
    "addon_price_ils",
    "unit_price_ils",
    "price_amount",
  ];

  let adultPrice =
    pickPositive(rule?.price_adult) ?? pickPositive(rule?.priceAdult);

  if (adultPrice == null) {
    adultPrice = firstPositiveFromKeys(merged, adultKeysNoScalar);
  }

  const ps = String(merged.price_strategy ?? "").toLowerCase();
  if (adultPrice == null && pickPositive(merged.sell_value)) {
    if (ps === "" || ps === "fixed" || ps === "override" || ps === "attraction_price") {
      adultPrice = pickPositive(merged.sell_value);
    }
  }
  if (adultPrice == null && (ps === "fixed" || ps === "override")) {
    adultPrice = pickPositive(merged.price_value);
  }

  const pricingModel = String(
    merged.default_pricing_model || merged.pricing_model || ""
  ).toLowerCase();

  if (pricingModel === "per_person") {
    if (adultPrice == null) {
      adultPrice = pickPositive(merged.base_price);
    }
  }

  if (adultPrice == null && rule && typeof rule === "object") {
    adultPrice = pickScalarUnitPriceFromApi(rule, merged);
  }

  if (adultPrice == null) {
    const single = resolveOptionalUnitPriceIls(
      { ...merged, catalog, attraction_id: rule?.attraction_id, id: rule?.id },
      null
    );
    if (single != null) {
      adultPrice = single;
    }
  }

  let { childEligible, childUnitPrice } = resolveChildUnitFromSources(rule, merged, catalog);

  /**
   * SSOT מה-API: לעיתים מחיר מבוגר ב-sell_value ומחיר ילד ב-price_value, בעוד price_child נשאר null.
   * רק כשהערכים שונים ממחיר המבוגר שכבר נפתר — לא משכפלים מבוגר לילד.
   */
  if (!childEligible && adultPrice != null) {
    const ps = String(merged.price_strategy ?? (rule && rule.price_strategy) ?? "").toLowerCase();
    const pv =
      pickNonNegativeFinite(merged.price_value) ??
      pickNonNegativeFinite(rule?.price_value) ??
      pickNonNegativeFinite(catalog?.price_value);
    const sv =
      pickNonNegativeFinite(merged.sell_value) ??
      pickNonNegativeFinite(rule?.sell_value) ??
      pickNonNegativeFinite(catalog?.sell_value);
    const pmRule = String(merged.pricing_mode ?? rule?.pricing_mode ?? catalog?.pricing_mode ?? "").toLowerCase();
    const cv =
      pickNonNegativeFinite(merged.cost_value) ??
      pickNonNegativeFinite(rule?.cost_value) ??
      pickNonNegativeFinite(catalog?.cost_value);
    if (
      (ps === "fixed" || ps === "override" || ps === "attraction_price") &&
      pv != null &&
      pv !== adultPrice
    ) {
      childEligible = true;
      childUnitPrice = pv;
    } else if (
      (ps === "fixed" || ps === "override" || ps === "attraction_price") &&
      pmRule === "separate" &&
      cv != null &&
      cv !== adultPrice &&
      merged?.price_child == null &&
      (catalog == null || catalog.price_child == null)
    ) {
      /**
       * חוקי package ב־GET /rules (slim): ללא price_child / קטלוג; במצב separate מחיר הילד מגיע ב-cost_value
       * (באדמין: שדה cost_value לצד sell_value / price_value).
       */
      childEligible = true;
      childUnitPrice = cv;
    } else if (
      (ps === "fixed" || ps === "override" || ps === "attraction_price") &&
      pricingModel === "per_person" &&
      pv != null &&
      sv != null &&
      pv === sv &&
      pv === adultPrice &&
      merged?.price_child == null &&
      (catalog == null || catalog.price_child == null)
    ) {
      /** API: sell_value = price_value = מחיר ליחידה; price_child נשאר null — ילד באותו תעריף כמו מבוגר */
      childEligible = true;
      childUnitPrice = adultPrice;
    }
  }

  const childPrice = childEligible ? childUnitPrice : null;

  return { adultPrice, childPrice, childEligible, currency };
}
